import { ExecutableAction, ExecutableActionParam } from '../utils/executable-action';
import {
  CodeEvaluation,
  EmptyEvaluation,
  Evaluate,
  IdEvaluation,
  LevelEvaluation,
  TypeEvaluation,
} from './evaluations';
import { getComposedParams } from './message-center-utils';
import { MessageCenterStateModel, MessageItem, MessageItemLevel, MessageItemType } from './message-center.model';
import { inject } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export class RemoveMessage implements ExecutableAction<MessageCenterStateModel> {
  static readonly type = '[Message Center] Remove Message';

  /**
   * Removes all error type messages
   */
  static AllErrors(): RemoveMessage {
    return new RemoveMessage(MessageItemType.error);
  }

  /**
   * Removes all warning type messages
   */
  static AllWarnings(): RemoveMessage {
    return new RemoveMessage(MessageItemType.warning);
  }

  /**
   * Removes all info type messages
   */
  static AllInfos(): RemoveMessage {
    return new RemoveMessage(MessageItemType.info);
  }

  /**
   * Removes all queue type messages
   */
  static AllQueues(): RemoveMessage {
    return new RemoveMessage(MessageItemType.queue);
  }

  /**
   * Removes the error matching by composed string
   * {level}.{id}.{code}
   */
  static Error(composition: string): RemoveMessage {
    return this.buildFromComposedString(MessageItemType.error, composition);
  }

  /**
   * Removes the warning matching by composed string
   * {level}.{id}.{code}
   */
  static Warning(composition: string): RemoveMessage {
    return this.buildFromComposedString(MessageItemType.warning, composition);
  }

  /**
   * Removes the info matching by composed string
   * {level}.{id}.{code}
   */
  static Info(composition: string): RemoveMessage {
    return this.buildFromComposedString(MessageItemType.info, composition);
  }

  /**
   * Removes the queue matching by composed string
   * {level}.{id}.{code}
   */
  static Queue(composition: string): RemoveMessage {
    return this.buildFromComposedString(MessageItemType.queue, composition);
  }

  /**
   * Removes the message by code
   */
  static ByCode(code: string): RemoveMessage | InvalidRemoveMessage {
    if (code == null || code === '') {
      return new InvalidRemoveMessage();
    }

    return new RemoveMessage(undefined, undefined, undefined, code);
  }

  /**
   * Removes the message by level, id and type
   * @param type of type MessageItemType
   * @param level of type MessageItemLevel
   * @param id as a number
   */
  static ByTypeLevelAndId(type: MessageItemType, level: MessageItemLevel, id: number): RemoveMessage {
    return new RemoveMessage(type, level, id, undefined);
  }

  /**
   * Removes the message by level and id
   * @param level of type MessageItemLevel
   * @param id as a number
   */
  static ByLevelAndId(level: MessageItemLevel, id: number): RemoveMessage {
    return new RemoveMessage(undefined, level, id, undefined);
  }

  private static buildFromComposedString(type: MessageItemType, composition: string) {
    const extractedParams = getComposedParams(composition);
    if (extractedParams.valid) {
      return new RemoveMessage(type, extractedParams.level, extractedParams.id, extractedParams.code);
    }
    return new InvalidRemoveMessage();
  }

  protected constructor(
    public type?: MessageItemType,
    public level?: MessageItemLevel,
    public id?: number,
    public code?: string
  ) {}

  public matches(item: MessageItem): boolean {
    return this.#buildEvaluation().executeEvaluation(item);
  }

  public execute(params: ExecutableActionParam<MessageItem[]>) {
    const state = params.ctx.getState();
    const evaluation = this.#buildEvaluation();
    const newMessages = state.filter(item => !evaluation.executeEvaluation(item as MessageItem));
    if (newMessages.length !== state.length) params.ctx.setState(newMessages);
  }

  #buildEvaluation(): Evaluate {
    let evaluation: Evaluate = new EmptyEvaluation();

    if (this.type != null) {
      evaluation = evaluation.AddEvaluation(new TypeEvaluation(this.type));
    }

    if (this.level != null) {
      evaluation = evaluation.AddEvaluation(new LevelEvaluation(this.level));
    }

    if (this.id != null) {
      evaluation = evaluation.AddEvaluation(new IdEvaluation(this.id));
    }

    if (this.code != null) {
      evaluation = evaluation.AddEvaluation(new CodeEvaluation(this.code));
    }

    return evaluation;
  }
}

class InvalidRemoveMessage extends RemoveMessage {
  public execute(_params: ExecutableActionParam<MessageCenterStateModel>) {
    const logger = inject(CbreLoggerToken) as CbreLogger;
    logger.error('Invalid message composition. The expected syntax is {level}.{id}.{code}');
  }
}
